import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { AuthUserProfile } from 'types/auth'
import { IFilter, Where } from 'types/common'
import { IEsGlobalSearchItem } from 'types/eslasticSearch'
import { searchGlobal } from 'API/search'
import {
  adminChangePassword,
  bindWorkEmail,
  countUsers as countUsersAPI,
  countUsersAuth,
  createUser as createUserAPI,
  getRecentlyViewed,
  getUserById,
  getUserDetailById,
  getUserList,
  getUsers as getUsersAPI,
  IUserListParams,
  resendWorkEmailVerification,
  updateUserById,
  verifyWorkEmail
} from 'API/user'
import {
  bulkCreateInvitations,
  BulkCreateInvitationsResult
} from 'API/invitation'
import { CreateInvitationPayload } from 'types/invitation'
import { AuthRoleIdEnum, AuthRoleNameEnum } from 'constants/user'
import { IGroupMember, IGroupMemberPermissionId } from 'interfaces/group'
import { IAuditWithRelations, ICreateEditUserRequest, IUser, IUserWithRelations } from 'interfaces/user'
import { getValidArray } from 'utils/common'
import { getFullName } from 'utils/users'
import { getRoleIdFromMembership } from 'utils/auth/role'
import { GlobalSearchDto } from './searchStore'
import { getError } from 'utils/getError'
import { EmailVerificationResponse, BackendSuccessResponse } from 'interfaces/common'

class UserStore {
  rootStore: RootStore

  currentUser: AuthUserProfile = {} as AuthUserProfile
  currentUserGroupMembers: IGroupMember[] = []

  users: IUserWithRelations[] = []
  numberOfUsers: number = 0

  usersWithOutPagination: IUserWithRelations[] = []

  isLoading: boolean = false
  // Separate flag for the member quick-view detail fetch so it never drives the
  // user-list spinner (otherwise opening the view modal blinks the whole list).
  isLoadingDetail: boolean = false
  isInviting: boolean = false

  filter: IFilter<IUser> = {}

  isManageModeInUserDetail = false
  canUserEditInUserDetail = true

  userDetail: IUserWithRelations = {}
  selectedUserDetail: IUserWithRelations = {}
  recentAudits: IAuditWithRelations[] = []
  isManageMode: boolean = false

  userDetailProcesses: IEsGlobalSearchItem[] = []
  countDetailProcess: number = 0

  userDetailCollections: IEsGlobalSearchItem[] = []
  countDetailCollection: number = 0

  // Email verification loading state
  isEmailVerifying: boolean = false

  // Sequence number of the latest `getUserList` call. The member list is refetched
  // on every tab / filter / page change, so two requests are regularly in flight at
  // once; without this, whichever response lands LAST wins and the Coach tab can end
  // up rendering the Player request's rows (and vice versa).
  private userListRequestId: number = 0

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)

    this.rootStore = rootStore
  }

  public setManageModeInUserDetail(isManageMode: boolean) {
    this.isManageModeInUserDetail = isManageMode ?? false
  }

  public setCanUserEditUserDetail(canEdit: boolean) {
    this.canUserEditInUserDetail = canEdit
  }

  public setCurrentUser(user: AuthUserProfile) {
    const effectiveRoleId = getRoleIdFromMembership(this.rootStore.authStore.currentMembership, user?.authRoleId)
    const haveEditorRole =
      this.currentUserGroupMembers.some(
        groupMember =>
          Number(groupMember?.permissionId ?? IGroupMemberPermissionId.VIEWER) < IGroupMemberPermissionId.VIEWER
      ) || effectiveRoleId < AuthRoleIdEnum.PLAYER
    this.currentUser = { ...user, authRoleId: effectiveRoleId, isEditor: haveEditorRole }
  }

  public setCurrentUserGroupMembers(groupMembers: IGroupMember[]) {
    this.currentUserGroupMembers = groupMembers
  }

  public async getUserDetail(userId: number, isSelected?: boolean) {
    // The quick-view modal (isSelected) loads into selectedUserDetail and must not
    // toggle the shared list spinner; detail pages keep using isLoading as before.
    if (isSelected) {
      this.isLoadingDetail = true
    } else {
      this.isLoading = true
    }
    const userDetail = await getUserDetailById(userId)
    if (isSelected) {
      this.selectedUserDetail = userDetail
    } else {
      this.userDetail = {
        ...userDetail,
        fullName: getFullName(userDetail?.firstName, userDetail?.lastName),
        image: userDetail?.image ?? ''
      }
    }
    if (isSelected) {
      this.isLoadingDetail = false
    } else {
      this.isLoading = false
    }
  }

  public async setUserListFilter(filter: IFilter<IUser & { keyword?: string }> = {}): Promise<void> {
    this.filter = filter
    await Promise.all([this.getUsers(filter), this.countUsers(filter?.where)])
  }

  public async refetchUserList(): Promise<void> {
    await Promise.all([this.getUsers(this.filter), this.countUsers(this.filter?.where)])
  }

  public async getUsers(filter: IFilter<IUser> = {}): Promise<void> {
    this.users = await getUsersAPI(filter)
  }

  public async countUsers(where: Where<IUser> = {}): Promise<number> {
    this.numberOfUsers = await countUsersAPI(where)
    return this.numberOfUsers
  }

  public async checkExistUser(where: Where<IUser> = {}): Promise<number> {
    this.numberOfUsers = await countUsersAuth(where)
    return this.numberOfUsers
  }

  public async updateUser(userId: number, userData: IUser = {}): Promise<void> {
    this.isLoading = true
    await updateUserById(userId, userData)
    this.isLoading = false
  }

  public async createUser(userData: ICreateEditUserRequest = {}): Promise<void> {
    this.isLoading = true
    try {
      await createUserAPI(userData)
    } finally {
      this.isLoading = false
    }
  }

  public async bulkInviteUsers(
    orgId: number,
    items: CreateInvitationPayload[]
  ): Promise<BulkCreateInvitationsResult> {
    this.isInviting = true
    try {
      return await bulkCreateInvitations(orgId, items)
    } finally {
      this.isInviting = false
    }
  }

  public async adminChangePassword(userId: number, newPassword: string): Promise<void> {
    this.isLoading = true
    await adminChangePassword({ userId, newPassword })
    this.isLoading = false
  }

  public async fetchRecentlyViewed(): Promise<void> {
    this.isLoading = true
    try {
      const result = await getRecentlyViewed()
      this.recentAudits = result || []
    } catch (error) {
      console.error('Failed to fetch recently viewed items:', error)
      this.recentAudits = []
    }
    this.isLoading = false
  }

  public async getUserList(params: IUserListParams): Promise<void> {
    const requestId = ++this.userListRequestId
    this.isLoading = true
    this.rootStore.spinnerStore.setLoading(true)
    try {
      const dataResponse = await getUserList(params)
      // A superseded response must not touch the list or the spinner: its rows
      // belong to a tab/filter the user has already left.
      if (requestId !== this.userListRequestId) return
      this.users = getValidArray(dataResponse?.results)
      this.numberOfUsers = dataResponse?.totalCount ?? 0
    } finally {
      if (requestId === this.userListRequestId) {
        this.isLoading = false
        this.rootStore.spinnerStore.setLoading(false)
      }
    }
  }

  public async getUsersForExport(): Promise<IUserWithRelations[]> {
    if (this.usersWithOutPagination.length === 0) {
      const dataResponse = await getUserList({
        pageSize: 0,
        pageIndex: 1,
        noPagination: true
      })
      const users = getValidArray(dataResponse?.results)
      this.usersWithOutPagination = users
      return users
    }
    return []
  }
  public setManageMode = (isManageMode: boolean): void => {
    this.isManageMode = isManageMode
  }

  public setDefaultUserListFilter(pageIndex: number, pageSize: number): void {
    const organizationId = this.rootStore.authStore.activeOrgId ?? 0
    this.filter = {
      where: {
        organizationId,
        isInactive: false
      },
      include: ['authRole', 'groupMembers'],
      skip: (pageIndex - 1) * pageSize,
      limit: pageSize,
      order: ['updatedAt DESC']
    }
  }

  public async getDetailProcess(filter: IFilter<GlobalSearchDto>): Promise<void> {
    try {
      this.isLoading = true

      const searchResult = await searchGlobal(filter)
      this.userDetailProcesses = searchResult.data
      this.countDetailProcess = searchResult.total

      this.isLoading = false
    } catch (error: any) {
      console.log('🚀 Log ~ file: userStore.ts Log ~ line 184 Log ~ UserStore Log error', error)
    }
  }

  public async getDetailCollection(filter: IFilter<GlobalSearchDto>): Promise<void> {
    try {
      this.isLoading = true

      const searchResult = await searchGlobal(filter)
      this.userDetailCollections = searchResult.data
      this.countDetailCollection = searchResult.total

      this.isLoading = false
    } catch (error: any) {
      console.log('🚀 Log ~ file: userStore.ts Log ~ line 184 Log ~ UserStore Log error', error)
    }
  }

  public clearStore(): void {
    this.userDetail = {} as IUserWithRelations
    this.userDetailProcesses = []
    this.countDetailProcess = 0
  }

  // Email verification methods
  public async bindWorkEmail(workEmail: string): Promise<EmailVerificationResponse> {
    this.isEmailVerifying = true
    try {
      const response: BackendSuccessResponse = await bindWorkEmail(workEmail)
      await this.rootStore.authStore.getMyUser()
      return { success: true, message: response?.message || 'Email verification code sent successfully.' }
    } catch (error: any) {
      console.log('🚀 Log ~ file: userStore.ts Log ~ line 237 Log ~ UserStore Log error', error)
      return { success: false, message: getError(error) }
    } finally {
      this.isEmailVerifying = false
    }
  }

  public async verifyWorkEmail(token: string): Promise<EmailVerificationResponse> {
    this.isEmailVerifying = true
    try {
      const response: BackendSuccessResponse = await verifyWorkEmail(token)
      await this.rootStore.authStore.getMyUser()
      return { success: true, message: response?.message || 'Email verified successfully.' }
    } catch (error: any) {
      console.log('🚀 Log ~ file: userStore.ts Log ~ line 247 Log ~ UserStore Log error', error)
      return { success: false, message: getError(error) }
    } finally {
      this.isEmailVerifying = false
    }
  }

  public async resendWorkEmailVerification(): Promise<EmailVerificationResponse> {
    this.isEmailVerifying = true
    try {
      const response: BackendSuccessResponse = await resendWorkEmailVerification()
      return { success: true, message: response?.message || 'Verification code resent successfully.' }
    } catch (error: any) {
      console.log('🚀 Log ~ file: userStore.ts Log ~ line 255 Log ~ UserStore Log error', error)
      return { success: false, message: getError(error) }
    } finally {
      this.isEmailVerifying = false
    }
  }
}

export default UserStore
